import React, { useEffect, useMemo, useState } from 'react';
import T from 'i18n';
import {
  TextField,
  Checkbox,
  InputAdornment,
  ListItemText,
  ListSubheader,
  MenuItem,
  Select,
  IconButton,
  CircularProgress,
} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import PerfectScrollbar from 'perfect-scrollbar';
import SearchIcon from '@mui/icons-material/Search';
import { makeStyles } from '@mui/styles';
import { containsText } from '../../utils';
import AddBoxIcon from '@mui/icons-material/AddBox';
import { useFetchTags } from 'features/case/redux/fetchTags';
import { createTag } from 'features/case/redux/CreateTag';
import { useDispatch } from 'react-redux';
import { fetchContainer } from 'features/case/redux/fetchContainer';

const useStyles = makeStyles(() => ({
  noBorder: {
    border: 'none',
  },
}));

export default ({
  value,
  placeholder,
  onChange,
  onBlur,
  readOnly,
  onEnter,
  onClosed,
  onOpen,
}: any) => {
  const [selectedOptions, setSelectedOptions] = useState<Array<any>>(value);
  const [searchText, setSearchText] = useState('');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const classes = useStyles();

  const dispatch = useDispatch();

  const { fetchTags, tags } = useFetchTags();

  //MOUNT
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => fetchTags(), []);

  const displayedOptions = useMemo(
    () => tags.filter((tag: any) => containsText(tag.name, searchText)),
    [searchText, tags],
  );

  const handleClickCreateTag = () => {
    if (!searchText || isSaving) return;
    setIsSaving(true);
    (dispatch as any)(createTag({ tag: { name: searchText } }))
      .then(() => {
        // Refresh quick links/containers after successful creation
        (dispatch as any)(fetchContainer({ folder: { type: 'tags' } }));
      })
      .finally(() => setIsSaving(false));
  };
  return (
    <Select
      // Disables auto focus on MenuItems and allows TextField to be in focus
      MenuProps={{
        autoFocus: false,
        sx: { zIndex: 100007 },
        PaperProps: {
          style: { zIndex: 100007 },
        },
        MenuListProps: {
          onKeyDown: (e: any) => {
            if (e && e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
              if (onEnter) onEnter();
            }
          },
        },
        onKeyDown: (e: any) => {
          if (e && e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            if (onEnter) onEnter();
          }
        },
      }}
      variant="outlined"
      sx={{
        boxShadow: 'none',
        '.MuiSelect-select': {
          ...(readOnly && { cursor: 'default' }),
        },
        '.MuiOutlinedInput-notchedOutline': { border: 0 },
        '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
          border: 0,
        },
        '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
          border: 0,
        },
      }}
      value={selectedOptions}
      label="Options"
      onBlur={onBlur}
      onChange={(e) => {
        setSelectedOptions(e.target.value as any);
        if (onChange) onChange(e.target.value as any);
      }}
      onOpen={() => {
        // only way I could find to get perfect scrollbar in
        let perfectScrollbar = null as any;
        setTimeout(() => {
          const paper = document.getElementsByClassName('MuiPaper-root')[0];
          perfectScrollbar = new PerfectScrollbar(paper);
        });
        setTimeout(() => perfectScrollbar?.update(), 250);
        setIsOpen(true);
        if (onOpen) onOpen();
      }}
      onKeyDown={(e: any) => {
        // Only intercept Enter when the select menu is open
        if (isOpen && e && e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          if (onEnter) onEnter();
        }
      }}
      onClose={() => {
        setSearchText('');
        setIsOpen(false);
        if (onClosed) onClosed();
      }}
      displayEmpty
      // This prevents rendering empty string in Select's value
      // if search text would exclude currently selected option.
      renderValue={() => {
        // we don't use selected cause we can have [undefined]
        if (selectedOptions.length === 0) {
          return <span style={{ opacity: 0.4, color: 'rgba(0, 0, 0, 0.87)' }}>{placeholder}</span>;
        }
        return tags.length > 0
          ? selectedOptions
              .map((option) => {
                const tag = tags.find(({ id }: any) => option === id);
                return tag?.name;
              })
              .join(', ')
          : '';
      }}
      multiple
      readOnly={readOnly}
      {...(readOnly && { IconComponent: () => null })}
    >
      {/* TextField is put into ListSubheader so that it doesn't
               act as a selectable item in the menu
               i.e. we can click the TextField without triggering any selection.*/}
      <ListSubheader sx={{ zIndex: 0, lineHeight: '1rem' }}>
        <TextField
          size="small"
          // Autofocus on textfield
          autoFocus
          autoComplete="off"
          variant="outlined"
          placeholder={placeholder}
          fullWidth
          InputProps={{
            classes: { notchedOutline: classes.noBorder },
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {isSaving ? (
                  <CircularProgress size={16} sx={{ color: '#7BA0F4' }} />
                ) : searchText &&
                  !displayedOptions
                    .map((tag: any) => tag.name.toLowerCase())
                    .includes(searchText.toLowerCase()) ? (
                  <Tooltip title={T.translate('viewing.createTag')} placement="top" arrow>
                    <IconButton onClick={handleClickCreateTag} edge="end" size="medium">
                      <AddBoxIcon fontSize="medium" sx={{ color: '#4567e5', '&:hover': { opacity: 0.85 } }} />
                    </IconButton>
                  </Tooltip>
                ) : null}
              </InputAdornment>
            ),
          }}
          onChange={(e) => {
            setSearchText(e.target.value);
          }}
          onKeyDown={(e) => {
            if (
              e.key === 'Enter' &&
              !isSaving &&
              searchText &&
              !displayedOptions
                .map((tag: any) => tag.name.toLowerCase())
                .includes(searchText.toLowerCase())
            ) {
              e.preventDefault();
              e.stopPropagation();
              handleClickCreateTag();
              return;
            }
            if (e.key !== 'Escape') {
              // Prevents autoselecting item while typing (default Select behaviour)
              e.stopPropagation();
            }
          }}
        />
      </ListSubheader>
      {!!displayedOptions?.length && (
          <ListSubheader sx={{ zIndex: 0, lineHeight: '1rem' }}>
            {searchText ? 'Search Results' : 'Tags'}
          </ListSubheader>
        )}
      {displayedOptions.map((tag: any) => (
        <MenuItem key={tag.id} value={tag.id}>
          <Checkbox checked={selectedOptions.includes(tag.id)} sx={{ padding: '0.25rem 0.5rem' }} />
          <ListItemText primary={tag.name} />
        </MenuItem>
      ))}
    </Select>
  );
};
